import React, { useRef, useState, useEffect } from 'react';
import { Search, Tune, Close, LocationOn } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  Menu,
  Typography,
  Divider,
  Slider,
  Button,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';

// Price ranges based on current path
const getPriceRanges = (path) => {
  const purchaseRanges = [
    { label: 'No Min', min: 0, max: 0 },
    { label: '10L', min: 1000000, max: 1000000 },
    { label: '20L', min: 2000000, max: 2000000 },
    { label: '30L', min: 3000000, max: 3000000 },
    { label: '40L', min: 4000000, max: 4000000 },
    { label: '50L', min: 5000000, max: 5000000 },
    { label: '60L', min: 6000000, max: 6000000 },
    { label: '70L', min: 7000000, max: 7000000 },
    { label: '80L', min: 8000000, max: 8000000 },
    { label: '90L', min: 9000000, max: 9000000 },
    { label: '1CR', min: 10000000, max: 10000000 },
    { label: '1.25CR', min: 12500000, max: 12500000 },
    { label: '1.5CR', min: 15000000, max: 15000000 },
    { label: '1.75CR', min: 17500000, max: 17500000 },
    { label: '2CR', min: 20000000, max: 20000000 },
    { label: '2.5CR', min: 25000000, max: 25000000 },
    { label: '3CR', min: 30000000, max: 30000000 },
    { label: '3.5CR', min: 35000000, max: 35000000 },
    { label: '4CR', min: 40000000, max: 40000000 },
    { label: '4.5CR', min: 45000000, max: 45000000 },
    { label: '5CR', min: 50000000, max: 50000000 },
    { label: '5.5CR', min: 55000000, max: 55000000 },
    { label: '6CR', min: 60000000, max: 60000000 },
    { label: '8CR', min: 80000000, max: 80000000 },
    { label: '9CR', min: 90000000, max: 90000000 },
    { label: '10CR', min: 100000000, max: 100000000 },
    { label: '15CR', min: 150000000, max: 150000000 },
    { label: '20CR', min: 200000000, max: 200000000 },
    { label: '25CR', min: 250000000, max: 250000000 },
    { label: '30CR', min: 300000000, max: 300000000 },
    { label: '40CR', min: 400000000, max: 400000000 },
    { label: '50CR', min: 500000000, max: 500000000 },
    { label: '60CR', min: 600000000, max: 600000000 },
    { label: '70CR', min: 700000000, max: 700000000 },
    { label: '80CR', min: 800000000, max: 800000000 },
    { label: '90CR', min: 900000000, max: 900000000 },
    { label: '100CR+++', min: 1000000000, max: 5000000000 },
  ];

  const rentRanges = [
    { label: 'No Min', min: 0, max: 0 },
    { label: '1K', min: 1000, max: 1000 },
    { label: '2K', min: 2000, max: 2000 },
    { label: '3K', min: 3000, max: 3000 },
    { label: '4K', min: 4000, max: 4000 },
    { label: '5K', min: 5000, max: 5000 },
    { label: '10K', min: 10000, max: 10000 },
    { label: '15K', min: 15000, max: 15000 },
    { label: '20K', min: 20000, max: 20000 },
    { label: '25K', min: 25000, max: 25000 },
    { label: '30K', min: 30000, max: 30000 },
    { label: '35K', min: 35000, max: 35000 },
    { label: '40K', min: 40000, max: 40000 },
    { label: '50K', min: 50000, max: 50000 },
    { label: '60K', min: 60000, max: 60000 },
    { label: '70K', min: 70000, max: 70000 },
    { label: '80K', min: 80000, max: 80000 },
    { label: '90K', min: 90000, max: 90000 },
    { label: '1L', min: 100000, max: 100000 },
    { label: '1.25L', min: 125000, max: 125000 },
    { label: '1.5L', min: 150000, max: 150000 },
    { label: '2L', min: 200000, max: 200000 },
    { label: '2.5L', min: 250000, max: 250000 },
    { label: '3L', min: 300000, max: 300000 },
    { label: '3.5L', min: 350000, max: 350000 },
    { label: '4L', min: 400000, max: 400000 },
    { label: '4.5L', min: 450000, max: 450000 },
    { label: '5L', min: 500000, max: 500000 },
    { label: '6L', min: 600000, max: 600000 },
    { label: '7L', min: 700000, max: 700000 },
    { label: '8L', min: 800000, max: 800000 },
    { label: '9L', min: 900000, max: 900000 },
    { label: '10L', min: 1000000, max: 1000000 },
    { label: '15L', min: 1500000, max: 1500000 },
    { label: '20L', min: 2000000, max: 2000000 },
    { label: '25L', min: 2500000, max: 2500000 },
    { label: '30L', min: 3000000, max: 3000000 },
    { label: '30+++', min: 3000001, max: 10000000 },
  ];

  const leaseRanges = [
    { label: 'No Min', min: 0, max: 0 },
    { label: '1L', min: 100000, max: 100000 },
    { label: '2L', min: 200000, max: 200000 },
    { label: '3L', min: 300000, max: 300000 },
    { label: '4L', min: 400000, max: 400000 },
    { label: '5L', min: 500000, max: 500000 },
    { label: '10L', min: 1000000, max: 1000000 },
    { label: '15L', min: 1500000, max: 1500000 },
    { label: '20L', min: 2000000, max: 2000000 },
    { label: '25L', min: 2500000, max: 2500000 },
    { label: '30L', min: 3000000, max: 3000000 },
    { label: '35L', min: 3500000, max: 3500000 },
    { label: '40L', min: 4000000, max: 4000000 },
    { label: '50L', min: 5000000, max: 5000000 },
    { label: '60L', min: 6000000, max: 6000000 },
    { label: '70L', min: 7000000, max: 7000000 },
    { label: '80L', min: 8000000, max: 8000000 },
    { label: '90L', min: 9000000, max: 9000000 },
    { label: '1CR', min: 10000000, max: 10000000 },
    { label: '1.25CR', min: 12500000, max: 12500000 },
    { label: '1.5CR', min: 15000000, max: 15000000 },
    { label: '2CR', min: 20000000, max: 20000000 },
    { label: '2.5CR', min: 25000000, max: 25000000 },
    { label: '3CR', min: 30000000, max: 30000000 },
    { label: '3.5CR', min: 35000000, max: 35000000 },
    { label: '4CR', min: 40000000, max: 40000000 },
    { label: '4.5CR', min: 45000000, max: 45000000 },
    { label: '5CR', min: 50000000, max: 50000000 },
    { label: '6CR', min: 60000000, max: 60000000 },
    { label: '7CR', min: 70000000, max: 70000000 },
    { label: '8CR', min: 80000000, max: 80000000 },
    { label: '9CR', min: 90000000, max: 90000000 },
    { label: '10CR', min: 100000000, max: 100000000 },
    { label: '15CR', min: 150000000, max: 150000000 },
    { label: '20CR', min: 200000000, max: 200000000 },
    { label: '25CR', min: 250000000, max: 250000000 },
    { label: '30CR', min: 300000000, max: 300000000 },
    { label: '30+++', min: 300000001, max: 1000000000 },
  ];

  if (path.includes('/buy-propertymap')) {
    return purchaseRanges;
  } else if (path.includes('/rent-propertymap')) {
    return rentRanges;
  } else if (path.includes('/lease_map')) {
    return leaseRanges;
  }
  
  return purchaseRanges; // default fallback
};

const getRangeButtons = (path) => {
  const purchaseRangeButtons = [
    { label: 'No Min', min: 0, max: 0 },

    { label: '0 - 10L', min: 0, max: 1000000 },
    { label: '10L - 20L', min: 1000000, max: 2000000 },
    { label: '20L - 30L', min: 2000000, max: 3000000 },
    { label: '30L - 40L', min: 3000000, max: 4000000 },
    { label: '40L - 50L', min: 4000000, max: 5000000 },
    { label: '50L - 60L', min: 5000000, max: 6000000 },
    { label: '60L - 70L', min: 6000000, max: 7000000 },
    { label: '70L - 80L', min: 7000000, max: 8000000 },
    { label: '80L - 90L', min: 8000000, max: 9000000 },
    { label: '90L - 1CR', min: 9000000, max: 10000000 },

    { label: '1CR - 1.5CR', min: 10000000, max: 15000000 },
    { label: '1.5CR - 1.75CR', min: 15000000, max: 17500000 },
    { label: '1.75CR - 2CR', min: 17500000, max: 20000000 },

    { label: '2CR - 2.5CR', min: 20000000, max: 25000000 },
    { label: '2.5CR - 3CR', min: 25000000, max: 30000000 },
    { label: '3CR - 3.5CR', min: 30000000, max: 35000000 },

    { label: '3.5CR - 4CR', min: 35000000, max: 40000000 },
    { label: '4CR - 4.5CR', min: 40000000, max: 45000000 },
    { label: '4.5CR - 5CR', min: 45000000, max: 50000000 },

    { label: '5CR - 5.5CR', min: 50000000, max: 55000000 },
    { label: '5.5CR - 6CR', min: 55000000, max: 60000000 },
    { label: '6CR - 8CR', min: 60000000, max: 80000000 },

    { label: '8CR - 10CR', min: 80000000, max: 100000000 },
    { label: '10CR - 15CR', min: 100000000, max: 150000000 },
    { label: '15CR - 20CR', min: 150000000, max: 200000000 },

    { label: '20CR - 25CR', min: 200000000, max: 250000000 },
    { label: '25CR - 30CR', min: 250000000, max: 300000000 },
    { label: '30CR - 40CR', min: 300000000, max: 400000000 },

    { label: '40CR - 50CR', min: 400000000, max: 500000000 },
    { label: '50CR - 60CR', min: 500000000, max: 600000000 },
    { label: '60CR - 70CR', min: 600000000, max: 700000000 },

    { label: '70CR - 80CR', min: 700000000, max: 800000000 },
    { label: '80CR - 90CR', min: 800000000, max: 900000000 },
    { label: '90CR - 100CR', min: 900000000, max: 1000000000 },
    { label: '100CR++', min: 900000000, max: 5000000000 },

  ];

const rentRangeButtons = [
  { label: '0 - 1K', min: 0, max: 1000 },
  { label: '1K - 2K', min: 1000, max: 2000 },
  { label: '2K - 3K', min: 2000, max: 3000 },
  { label: '3K - 4K', min: 3000, max: 4000 },
  { label: '4K - 5K', min: 4000, max: 5000 },
  { label: '5K - 10K', min: 5000, max: 10000 },
  { label: '10K - 15K', min: 10000, max: 15000 },
  { label: '15K - 20K', min: 15000, max: 20000 },
  { label: '20K - 25K', min: 20000, max: 25000 },
  { label: '25K - 30K', min: 25000, max: 30000 },
  { label: '30K - 40K', min: 30000, max: 40000 },
  { label: '40K - 50K', min: 40000, max: 50000 },
  { label: '50K - 60K', min: 50000, max: 60000 },
  { label: '60K - 70K', min: 60000, max: 70000 },
  { label: '70K - 80K', min: 70000, max: 80000 },
  { label: '80K - 90K', min: 80000, max: 90000 },
  { label: '90K - 1L', min: 90000, max: 100000 },

  { label: '1L - 1.25L', min: 100000, max: 125000 },
  { label: '1.25L - 1.5L', min: 125000, max: 150000 },
  { label: '1.5L - 2L', min: 150000, max: 200000 },
  { label: '2L - 2.5L', min: 200000, max: 250000 },
  { label: '2.5L - 3L', min: 250000, max: 300000 },
  { label: '3L - 3.5L', min: 300000, max: 350000 },
  { label: '3.5L - 4L', min: 350000, max: 400000 },
  { label: '4L - 4.5L', min: 400000, max: 450000 },
  { label: '4.5L - 5L', min: 450000, max: 500000 },

  { label: '5L - 6L', min: 500000, max: 600000 },
  { label: '6L - 7L', min: 600000, max: 700000 },
  { label: '7L - 8L', min: 700000, max: 800000 },
  { label: '8L - 9L', min: 800000, max: 900000 },
  { label: '9L - 10L', min: 900000, max: 1000000 },

  { label: '10L - 15L', min: 1000000, max: 1500000 },
  { label: '15L - 20L', min: 1500000, max: 2000000 },
  { label: '20L - 25L', min: 2000000, max: 2500000 },
  { label: '25L - 30L', min: 2500000, max: 3000000 },

  { label: '30L+++', min: 3000000, max: 10000000 },
];

  const leaseRangeButtons = [
    { label: '0 - 1L', min: 0, max: 100000 },
    { label: '1L- 2L', min: 100000, max: 200000 },
    { label: '2L - 3L', min: 200000, max: 300000 },
    { label: '3L - 4L', min: 300000, max: 400000 },
    { label: '4L- 5L', min: 400000, max: 500000 },
    { label: '5L - 10L', min: 500000, max: 1000000 },
    { label: '10L - 15L', min: 1000000, max: 1500000 },
    { label: '15L - 20L', min: 1500000, max: 2000000 },
    { label: '20L - 25L', min: 2000000, max: 2500000 },
    { label: '25L - 30L', min: 2500000, max: 3000000 },
    { label: '30L - 35L', min: 3000000, max: 3500000 },
    { label: '35L - 40L', min: 3500000, max: 4000000 },
    { label: '40L - 50L', min: 4000000, max: 5000000 },
    { label: '50L - 60L', min: 5000000, max: 6000000 },
    { label: '60L - 70L', min: 6000000, max: 7000000 },
    { label: '70L - 80L', min: 7000000, max: 8000000 },
    { label: '80L - 90L', min: 8000000, max: 9000000 },
    { label: '90L - 1CR', min: 9000000, max: 10000000 },
    { label: '1CR - 1.25CR', min: 10000000, max: 12500000 },
    { label: '1.25CR - 1.5CR', min: 12500000, max: 15000000 },

    { label: '1.5CR - 2CR', min: 15000000, max: 20000000 },
    { label: '2CR - 2.5CR', min: 20000000, max: 25000000 },
    { label: '3CR - 3.5CR', min: 30000000, max: 35000000 },
    { label: '4CR - 4.5CR', min: 40000000, max: 45000000 },
    { label: '5CR - 6CR', min: 50000000, max: 60000000 },
    { label: '6CR - 7CR', min: 60000000, max: 70000000 },
    { label: '7CR - 8CR', min: 70000000, max: 80000000 },
    { label: '8CR - 9CR', min: 80000000, max: 90000000 },
    { label: '9CR - 10CR', min: 90000000, max: 100000000 },
    { label: '10CR - 15CR', min: 100000000, max: 150000000 },
    { label: '15CR - 20CR', min: 150000000, max: 200000000 },
    { label: '20CR - 25CR', min: 200000000, max: 250000000 },
    { label: '25CR - 30CR', min: 250000000, max: 300000000 },
    { label: '30CR+++', min: 300000001, max: 1000000000 },
  ];

  if (path.includes('/buy-propertymap')) {
    return purchaseRangeButtons;
  } else if (path.includes('/rent-propertymap')) {
    return rentRangeButtons;
  } else if (path.includes('/lease_map')) {
    return leaseRangeButtons;
  }
  
  return purchaseRangeButtons;
};

const CustomSearchBar = ({ onPlaceSelected, onClear, onPriceFilterChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const paperRef = useRef(null);
  const autocompleteRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [tempSelectedRange, setTempSelectedRange] = useState(null);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [customRange, setCustomRange] = useState([0, 5000000000]);
  const [sliderMax, setSliderMax] = useState(5000000000);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [predictions, setPredictions] = useState([]);

  const GOOGLE_MAPS_API_KEY = GOOGLEMAPS_API_KEY;
  
  // Get price ranges based on current path
  const currentPriceRanges = getPriceRanges(location.pathname);
  const currentRangeButtons = getRangeButtons(location.pathname);

  const formatPrice = (value) => {
    if (value === 0) return 'No Min';
    if (value >= 10000000) return `${value / 10000000} Cr`;
    if (value >= 100000) return `${value / 100000} L`;
    if (value >= 1000) return `${value / 1000} K`;
    return value;
  };

  useEffect(() => {
    const savedSearch = localStorage.getItem('savedSearchValue');
    if (savedSearch) setSearchValue(savedSearch);

    const savedSearches = localStorage.getItem('recentLocationSearches');
    if (savedSearches) setRecentSearches(JSON.parse(savedSearches));

    if (window.google?.maps?.places) {
      setApiLoaded(true);
      initAutocomplete();
    } else {
      loadGoogleMapsAPI();
    }
  }, []);

  const loadGoogleMapsAPI = () => {
    if (document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setApiLoaded(true);
      initAutocomplete();
    };
    document.head.appendChild(script);
  };

  const initAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    autocompleteRef.current = new window.google.maps.places.AutocompleteService();

    const handleInputChange = () => {
      const value = inputRef.current?.value || '';
      setSearchValue(value);

      if (!value.trim()) {
        setPredictions([]);
        setSuggestionsOpen(false);
        return;
      }

      autocompleteRef.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'in' },
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
            setSuggestionsOpen(true);
          } else {
            setPredictions([]);
            setSuggestionsOpen(false);
          }
        }
      );
    };

    inputRef.current.addEventListener('input', handleInputChange);

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener('input', handleInputChange);
      }
    };
  };

  const handlePlaceSelected = (place) => {
    if (!place.geometry) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: place.place_id }, (fullPlace, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          processPlace(fullPlace);
        }
      });
    } else {
      processPlace(place);
    }
  };

  const processPlace = (place) => {
    const locationData = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      address: place.formatted_address || place.name,
      name: place.name || place.formatted_address,
      radius: 10000,
    };

    setRecentSearches((prev) => [
      locationData,
      ...prev.filter((item) => item.address !== locationData.address).slice(0, 4),
    ]);

    if (onPlaceSelected) onPlaceSelected(locationData);
    setSearchValue(locationData.name);
    setSuggestionsOpen(false);
    setPredictions([]);
  };

  const handleSuggestionClick = (prediction) => {
    setSearchValue(prediction.description);
    setSuggestionsOpen(false);

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails({ placeId: prediction.place_id }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        handlePlaceSelected(place);
      }
    });
  };

  const handleTuneClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  useEffect(() => {
    if (anchorEl) setTempSelectedRange(selectedPriceRange);
  }, [anchorEl]);

  const handlePriceRangeSelectTemp = (range) => {
    setCustomRange([range.min, range.max]);
    setTempSelectedRange({
      label: range.label,
      min: range.min,
      max: range.max,
    });
    setSliderMax(range.max > sliderMax ? range.max : sliderMax);
  };

  const handleRangeButtonSelect = (range) => {
    setCustomRange([range.min, range.max]);
    setTempSelectedRange({
      label: range.label,
      min: range.min,
      max: range.max,
    });
    setSliderMax(range.max > sliderMax ? range.max : sliderMax);
  };

  const handleSliderChangeTemp = (e, newValue) => {
    setCustomRange(newValue);
    setTempSelectedRange({
      label: `${formatPrice(newValue[0])} - ${formatPrice(newValue[1])}`,
      min: newValue[0],
      max: newValue[1],
    });
    if (newValue[1] > sliderMax) setSliderMax(newValue[1]);
  };

  const handleApplyFilter = () => {
    setSelectedPriceRange(tempSelectedRange);
    if (onPriceFilterChange) onPriceFilterChange(tempSelectedRange);
    handleMenuClose();
  };

  const clearPriceFilter = () => {
    setSelectedPriceRange(null);
    setTempSelectedRange(null);
    setCustomRange([0, 1000000000]);
    setSliderMax(1000000000);
    if (onPriceFilterChange) onPriceFilterChange(null);
    handleMenuClose();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Main Search Bar */}
      <Paper
        ref={paperRef}
        component="form"
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: '30px',
          px: 1,
          backgroundColor: '#f2f5f7',
          border: '1px solid black',
          mx: 0.5,
          my: 0.5,
        }}
        onSubmit={(e) => e.preventDefault()}
      >
        <IconButton onClick={() => navigate(-1)} sx={{ color: 'black' }}>
          <ArrowBackIosNewIcon />
        </IconButton>

        <InputBase
          inputRef={inputRef}
          sx={{ ml: 1, flex: 1, fontSize: '14px' }}
          placeholder="Search location..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={() => searchValue && setSuggestionsOpen(true)}
        />

        {searchValue && (
          <IconButton onClick={() => {
            setSearchValue('');
            setSuggestionsOpen(false);
            setPredictions([]);
          }} sx={{ color: 'black' }}>
            <Close fontSize="small" />
          </IconButton>
        )}

        <IconButton type="submit" sx={{ color: 'black' }}>
          <Search />
        </IconButton>

        <IconButton sx={{ color: 'black' }} onClick={handleTuneClick}>
          <Tune />
        </IconButton>
      </Paper>

      {/* Full-Width Suggestions Dropdown */}
      <Popper
        open={suggestionsOpen && predictions.length > 0}
        anchorEl={paperRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: '100%', maxWidth: 480, mx: 'auto' }}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: { boundary: 'clippingParents' },
          },
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
      >
        <ClickAwayListener onClickAway={() => setSuggestionsOpen(false)}>
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              maxWidth: { xs: 'calc(100vw - 16px)', sm: 480 },
              mx: 'auto',
              borderRadius: '12px',
              overflow: 'hidden',
              mt: 0.5,
            }}
          >
            <List>
              {predictions.map((prediction, index) => (
                <ListItem
                  key={index}
                  button
                  onClick={() => handleSuggestionClick(prediction)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <LocationOn color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={prediction.description}
                    primaryTypographyProps={{ fontSize: '14px' }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Recent Searches Dropdown */}
      {recentSearches.length > 0 && (
        <Popper
          open={suggestionsOpen && predictions.length === 0 && searchValue === ''}
          anchorEl={paperRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300, width: '100%', maxWidth: 480, mx: 'auto' }}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: true,
              options: { boundary: 'clippingParents' },
            },
            {
              name: 'offset',
              options: { offset: [0, 8] },
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setSuggestionsOpen(false)}>
            <Paper
              elevation={6}
              sx={{
                width: '100%',
                maxWidth: { xs: 'calc(100vw - 16px)', sm: 480 },
                mx: 'auto',
                borderRadius: '12px',
                overflow: 'hidden',
                mt: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
                Recent Searches
              </Typography>
              <List>
                {recentSearches.map((search, index) => (
                  <ListItem
                    key={index}
                    button
                    onClick={() => {
                      setSearchValue(search.name);
                      if (onPlaceSelected) onPlaceSelected(search);
                      setSuggestionsOpen(false);
                    }}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationOn color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={search.name}
                      secondary={search.address}
                      primaryTypographyProps={{ fontSize: '14px' }}
                      secondaryTypographyProps={{ fontSize: '12px' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}

      {/* Price Filter Menu */}
      <Menu
        id="filter-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ 
          style: { 
            width: '380px', 
            maxHeight: '500px',
            padding: '8px 0' 
          } 
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
          Select Price Range
        </Typography>
        <Divider />

        {/* Slider Section - MOVED TO TOP */}
        <Box sx={{ px: 2, py: 2 }}>
          <Slider
            value={customRange}
            onChange={handleSliderChangeTemp}
            min={0}
            max={sliderMax}
            step={1000}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatPrice(value)}
            className="MuiSlider-root"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption">{formatPrice(customRange[0])}</Typography>
            <Typography variant="caption">{formatPrice(customRange[1])}</Typography>
          </Box>
        </Box>

        <Divider />

        {/* Range Buttons Section - 3 COLUMNS LAYOUT */}
        <Box sx={{ px: 2, py: 2 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 1,
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {currentRangeButtons.map((range) => (
              <Button
                key={range.label}
                variant={tempSelectedRange?.label === range.label ? 'contained' : 'outlined'}
                size="small"
                sx={{ 
                  borderRadius: '16px', 
                  textTransform: 'none', 
                  fontSize: '0.7rem',
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
                onClick={() => handleRangeButtonSelect(range)}
              >
                {range.label}
              </Button>
            ))}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1 }}>
          <Button onClick={clearPriceFilter} color="error">
            CLEAR
          </Button>
          <Button onClick={handleApplyFilter} variant="contained" color="primary">
            APPLY
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default CustomSearchBar;